import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import * as moment from 'moment';

import { ScheduledEvent } from '../../../../../../models/scheduled-event';
import { Customer, CustomerSearchResult } from '../../../../../../models/customer';
import { dateStringFromISO8601, getAge, getISO8601DateStringFromMoment, propExists } from '../../../../../../utils';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerService } from '../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { MembershipService } from '../../../../../../services/membership/membership.service';
import { EventType, Roster } from '../../../../../../models/event-type';
import { Membership } from '../../../../../../models/membership';
import { MembershipPurchaseType } from '../../../../../../models/membership-enums';
import { debounceTime } from 'rxjs/operators';
import { openClimberAppInNewTabAsCustomer, openClimberAppInNewTabAsStaff } from '../../../../../../utils-models';


@Component({
  selector: 'app-add-to-roster-modal',
  templateUrl: './add-to-roster-modal.component.html',
  styleUrls: ['./add-to-roster-modal.component.css']
})
export class AddToRosterModalComponent implements OnInit {
  @Input() eventTypeId: number;
  @Input() eventTypeName: string;
  @Input() preselectedRosterId: number = null;
  @Input() preselectedOwnerCustomerId: number = null;
  @Input() preselectedParticipantCustomerId: number = null;

  public rosters: Roster[];
  public eventType: EventType;
  public pricingMembership: Membership | null = null;
  public hasFixedRoster: boolean;
  public hasFixedOwnerCustomer: boolean;
  public hasFixedParticipantCustomer: boolean;

  public selectedOwnerCustomer: CustomerSearchResult = null;
  public selectedParticipantCustomer: Customer = null;
  public selectedRoster: Roster = null;
  public ageBlockedOwnerCustomer: CustomerSearchResult | null = null;

  public loadingSubmit = false;
  public loadingOwnerCustomers = false;
  public loadingParticipantCustomer = false;
  public loadingRosters = true;
  public loadingEventType = true;

  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: CustomerSearchResult[] | null;

  public errorMessage = '';
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public activeModal: NgbActiveModal,
    private customerService: CustomerService,
    public orgService: OrganizationConfigService,
    private eventTypeService: EventTypeService,
    private membershipService: MembershipService,
  ) {}

  ngOnInit() {
    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });

    this.hasFixedRoster = (this.preselectedRosterId !== null);
    this.hasFixedOwnerCustomer = (this.preselectedOwnerCustomerId !== null);
    this.hasFixedParticipantCustomer = (this.preselectedParticipantCustomerId !== null);

    this.loadRosters();
    this.loadEventType();

    if (this.hasFixedOwnerCustomer) {
      this.loadPreselectedOwnerCustomer();
    }
    if (this.hasFixedParticipantCustomer) {
      this.loadPreselectedParticipantCustomer();
    }
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      (this.customerList.length > 0)
    );
  }

  public selectCustomer(customer: CustomerSearchResult) {
    this.selectedOwnerCustomer = customer;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
    this.checkOwnerAge();
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingOwnerCustomers = true;
    this.selectedOwnerCustomer = null;

    const orgConfig = this.orgService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public isLoading() {
    return (
      this.loadingSubmit ||
      this.loadingOwnerCustomers ||
      this.loadingRosters ||
      this.loadingEventType
    );
  }

  public submitButtonAvailable() {
    if (!this.selectedRoster) { return false; }

    if (this.hasFixedParticipantCustomer && !this.selectedOwnerCustomer) {
      return false;
    }

    if (this.ageBlockedOwnerCustomer) { return false; }

    return true;
  }

  public handleSubmit() {
    if (this.isLoading()) { return; }
    if (!this.selectedRoster) { return; }

    this.loadingSubmit = true;
    this.errorMessage = '';

    const baseUrl = this.orgService.getOrganizationConfig().climber_app_base_url;
    let purchaseUrl: string;
    if (this.selectedParticipantCustomer === null) {
      purchaseUrl = (
        baseUrl +
        'membership/choose-members/' +
        this.eventType.pricing_membership_id.toString() +
        '/?roster=' +
        this.selectedRoster.id.toString()
      );
    } else {
      purchaseUrl = (
        baseUrl +
        'membership/initialize-for-roster-participant/' +
        this.eventType.pricing_membership_id.toString() + '/' +
        this.selectedRoster.id.toString() + '/' +
        this.preselectedParticipantCustomerId.toString() + '/'
      );
    }

    if (this.selectedOwnerCustomer === null) {
      this.customerService
        .getStaffClimberAppToken()
        .then(token => {
          openClimberAppInNewTabAsStaff(
            token,
            baseUrl,
            purchaseUrl
          );
          this.activeModal.close();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to take this action.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    } else {
      this.customerService
        .getCustomerClimberAppToken(this.selectedOwnerCustomer.id)
        .then(token => {
          openClimberAppInNewTabAsCustomer(
            token,
            this.selectedOwnerCustomer.id,
            baseUrl,
            purchaseUrl
          );
          this.activeModal.close();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to take this action.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    }
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingOwnerCustomers = true;
    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  private loadCustomers() {
    this.loadingOwnerCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingOwnerCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response.results;
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingOwnerCustomers = false;
        }
      });
  }

  private loadRosters() {
    this.loadingRosters = true;

    this.eventTypeService
      .getRosters(
        1,
        3000,
        {event_type_id: this.eventTypeId}
      )
      .then(result => {
        this.rosters = result.results;
        if (this.rosters.length === 1) {
          this.selectedRoster = this.rosters[0];
        }
        if (this.preselectedRosterId) {
          for (const roster of this.rosters) {
            if (roster.id === this.preselectedRosterId) {
              this.selectedRoster = roster;
            }
          }
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingRosters = false; });
  }

  private loadEventType() {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(this.eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
        if (eventType.pricing_membership_id) {
          this.membershipService
            .getMembership(eventType.pricing_membership_id)
            .then(m => { this.pricingMembership = m; });
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingEventType = false; });
  }

  private checkOwnerAge() {
    this.ageBlockedOwnerCustomer = null;
    const orgConfig = this.orgService.getOrganizationConfig();
    if (!orgConfig.membership_minimum_age || !this.selectedOwnerCustomer?.birthday) { return; }
    const isRestricted = (
      this.pricingMembership?.purchase_type === MembershipPurchaseType.recurring ||
      orgConfig.membership_minimum_age_applies_to_prepaid
    );
    if (!isRestricted) { return; }
    if (getAge(this.selectedOwnerCustomer.birthday) < orgConfig.membership_minimum_age) {
      this.ageBlockedOwnerCustomer = this.selectedOwnerCustomer;
    }
  }

  private loadPreselectedOwnerCustomer() {
    this.loadingOwnerCustomers = true;
    this.search = 'Loading...';

    this.customerService
      .getCustomer(this.preselectedOwnerCustomerId)
      .then(customer => {
        this.selectedOwnerCustomer = {
          id: customer.id,
          email: customer.email,
          first_name: customer.first_name,
          last_name: customer.last_name,
          preferred_name: customer.preferred_name,
          birthday: customer.birthday
        };
        this.search = (
          customer.first_name + ' ' + customer.last_name
        );
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingOwnerCustomers = false; });
  }

  private loadPreselectedParticipantCustomer() {
    this.loadingParticipantCustomer = true;

    this.customerService
      .getCustomer(this.preselectedParticipantCustomerId)
      .then(customer => {
        this.selectedParticipantCustomer = customer;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingParticipantCustomer = false; });
  }
}
